import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Typography,
  Form,
  Input,
  Button,
  Divider,
  Space,
  Alert,
  Row,
  QRCode,
  Col,
  Spin,
  QRCodeProps,
  Select,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ReloadOutlined,
} from "@ant-design/icons";
import { campaignService } from "../../services/campaignService";
import CustomModal from "./Modal";
import { optionsContactIdd } from "../../constants/options";
import { isValidMobileNumber } from "../../utils/phoneValidation";
// import { browserLog } from "../../utils/log";

const { Title, Text } = Typography;

// After this window the QR auto-refresh stops and a manual retry is required.
const QR_RETRY_TIMEOUT_MS = 3 * 60 * 1000;

const formatCountdown = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export type WhatsAppConfigModalProps = {
  open: boolean;
  onCancel: () => void;
  onContinue: (values: {
    clientId: string;
    email: string;
    contactIdd: string;
    contactNumber: string;
  }) => void;
  defaultEmail?: string;
};

const WhatsAppConfigModal: React.FC<WhatsAppConfigModalProps> = ({
  open,
  onCancel,
  onContinue,
  defaultEmail = "",
}) => {
  const [form] = Form.useForm<{
    clientId: string;
    email: string;
    contactIdd: string;
    contactNumber: string;
  }>();
  const [checking, setChecking] = useState(false);
  const [loadingQR, setLoadingQR] = useState(false);
  const [expiredQR, setExpiredQR] = useState(false);
  const [active, setActive] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [valueQR, setValueQR] = useState("");
  const [qrTimedOut, setQrTimedOut] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(
    QR_RETRY_TIMEOUT_MS / 1000,
  );
  const pollingRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const [hasErrors, setHasErrors] = useState(true);

  const clearTimers = () => {
    pollingRef.current = false;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  // Starts the 3-minute window. When it elapses we stop the auto-refresh loop
  // and surface a manual retry button.
  const startSessionTimer = () => {
    if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    setQrTimedOut(false);
    setRemainingSeconds(QR_RETRY_TIMEOUT_MS / 1000);
    countdownRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    sessionTimeoutRef.current = setTimeout(() => {
      clearTimers();
      setLoadingQR(false);
      setQrTimedOut(true);
    }, QR_RETRY_TIMEOUT_MS);
  };

  const [step, setStep] = useState<0 | 1>(0);
  const handleBack = () => {
    clearTimers();
    setExpiredQR(false);
    setIsGenerating(false);
    setQrTimedOut(false);
    setStep(0);
  };

  const handleContinueEmail = async () => {
    await form.validateFields(["email"]);
    setStep(1);
    startSessionTimer();
    generateQR();
  };

  const handleRetry = () => {
    setIsGenerating(false);
    setLoadingQR(true);
    startSessionTimer();
    generateQR();
  };

  const handleConnect = async () => {
    setChecking(true);
    try {
      checkActivate(true);
    } finally {
      setChecking(false);
    }
  };

  const emailValue = Form.useWatch("email", form);
  const contactIdd = Form.useWatch("contactIdd", form);
  const contactNumber = Form.useWatch("contactNumber", form);

  const generateQR = async () => {
    if (emailValue) {
      try {
        setLoadingQR(true);
        const data1 = await campaignService.initClientId(
          contactIdd + contactNumber,
        );
        if (data1.status === "error") {
          // browserLog("warn", "Init error");

          return;
        } else {
          checkActivate();
          // browserLog("info", "Init check activate");
        }
      } catch (error) {
        console.error("Error generate QR:", error);
      } finally {
        setLoadingQR(false);
      }
    }
  };

  const generateQRCode = async (value: string): Promise<void> => {
    if (isGenerating) return;

    pollingRef.current = true;
    setIsGenerating(true);
    setExpiredQR(false);
    setLoadingQR(true);

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        timeoutRef.current = setTimeout(resolve, ms);
      });

    const tryGenerate = async (): Promise<void> => {
      if (!pollingRef.current) return;

      // browserLog("info", "Generate QR called");

      const data = await campaignService.generateQR(value);

      if (!pollingRef.current) return;

      // browserLog("info", "Generate QR response", data);

      if (data?.status === "expired") {
        // browserLog("info", "expired", data);

        setLoadingQR(true);
        await sleep(2000);
        return tryGenerate();
      }

      if (data?.status === "no_qr" && active) {
        // browserLog("info", "no_qr and active", data);
        setLoadingQR(true);
        await sleep(2000);
        return tryGenerate();
      }

      if (
        data?.status === "error" &&
        data?.message?.toLowerCase().includes("not initialized")
      ) {
        // browserLog("info", "not initialized", data);

        generateQR();
        await sleep(2000);
        return tryGenerate();
      }

      if (data != null && data?.status !== "no_qr") {
        const ttlMs = data.ttlMs ?? 2000;
        // browserLog("info", "data not null", data);
        setValueQR(data.qr);
        setLoadingQR(false);

        timeoutRef.current = setTimeout(() => {
          if (!pollingRef.current) return;
          setExpiredQR(true);
        }, ttlMs);
      }

      setIsGenerating(false);
      await sleep(2000);
      return tryGenerate();
    };

    await tryGenerate();
  };

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  const checkActivate = async (handleConnect: boolean = false) => {
    const data = await campaignService.checkActivate(
      contactIdd + contactNumber,
    );

    if (data?.active === true) {
      clearTimers();
      // browserLog("info", "data active ", data);

      setActive(true);
      onContinue({
        clientId: contactIdd + contactNumber,
        email: emailValue,
        contactIdd: contactIdd,
        contactNumber: contactNumber,
      });
    } else {
      if (handleConnect) {
        setIsErrorModalVisible(true);
      } else {
        setExpiredQR(true);
      }
    }
  };

  const customStatusRender: QRCodeProps["statusRender"] = (info) => {
    switch (info.status) {
      case "expired":
        return (
          <Space direction="vertical">
            <Spin size="large" />
            <p>Generating QR code...</p>
          </Space>
        );
      case "loading":
        return (
          <Space direction="vertical">
            <Spin size="large" />
            <p>Generating QR code...</p>
          </Space>
        );
      case "scanned":
        return (
          <div>
            <CheckCircleFilled style={{ color: "green" }} />{" "}
            {info.locale?.scanned}
          </div>
        );
      default:
        return (
          <Space direction="vertical">
            <Spin size="large" />
            <p>Generating QR code...</p>
          </Space>
        );
    }
  };

  useEffect(() => {
    if (expiredQR && contactIdd && contactNumber && step === 1 && !qrTimedOut) {
      generateQRCode(contactIdd + contactNumber);
    }
  }, [expiredQR, contactIdd, contactNumber, step, qrTimedOut]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={
        <span style={{ fontSize: 22, fontWeight: 600 }}>
          Activate WhatsApp Channel
        </span>
      }
      footer={null}
      width={640}
      style={{ top: 20 }}
      destroyOnClose
      afterClose={() => {
        clearTimers();
        setStep(0);
        setQrTimedOut(false);
        form.resetFields();
        // setVerified(false);
      }}
      maskClosable={false}
    >
      <Title level={4} style={{ textAlign: "center", marginBottom: 4 }}>
        {step === 0 ? "Set Your Contact Details" : "Scan QR Code"}
      </Title>
      <Text
        type="secondary"
        style={{ display: "block", textAlign: "center", marginBottom: 16 }}
      >
        {step === 0
          ? "Provide your email address and mobile number to complete the activation."
          : "Open your WhatsApp app and scan this QR code to link your account."}
      </Text>

      <Alert
        className="warning-configuration-whatsapp"
        type="warning"
        message={<strong>Prerequisite</strong>}
        description={
          <span>
            Integration requires a verified WhatsApp Business account.{" "}
            <a
              href="https://business.whatsapp.com/products/business-app-get-started"
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here
            </a>{" "}
            to verify or set up your account.
          </span>
        }
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Form
        form={form}
        requiredMark={(label, { required }) =>
          required ? (
            <>
              {label} <span style={{ color: "#ff4d4f" }}> {" *"}</span>
            </>
          ) : (
            label
          )
        }
        layout="vertical"
        style={{ marginTop: 12 }}
        initialValues={{ clientId: "", email: defaultEmail }}
        onFieldsChange={(_, allFields) => {
          const hasErrorOrEmpty = allFields.some(
            (f) =>
              (f.name?.[0] === "email" || f.name?.[0] === "contactNumber") &&
              ((f.errors?.length ?? 0) > 0 ||
                f.value === undefined ||
                f.value === null ||
                f.value === ""),
          );

          setHasErrors(hasErrorOrEmpty);
        }}
      >
        <div style={{ display: step === 0 ? "block" : "none" }}>
          <Form.Item
            label="Mobile Number"
            required
            extra={
              <Text type="secondary">
                This must be the WhatsApp-registered mobile number. You’ll scan
                a QR code next to connect and enable campaign sending.
              </Text>
            }
            style={{ marginBottom: 5 }}
          >
            <Space.Compact className="w-100">
              <Form.Item
                name="contactIdd"
                noStyle
                initialValue="65"
                rules={[{ required: true, message: "Select country code" }]}
              >
                <Select
                  style={{ width: 150 }}
                  options={optionsContactIdd}
                  placeholder="Code"
                  onChange={() => {
                    form.validateFields(["contactNumber"]);
                  }}
                />
              </Form.Item>

              <Form.Item
                name="contactNumber"
                noStyle
                validateTrigger="onChange"
                rules={[
                  { required: true, message: "Enter phone number" },
                  {
                    validator: async (_, value) => {
                      const code = form.getFieldValue("contactIdd");
                      if (!isValidMobileNumber(code, value)) {
                        return Promise.reject(
                          new Error("Please enter a valid mobile number"),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder="Enter Phone Number"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item
            label="Email Address"
            name="email"
            required
            validateTrigger="onChange"
            extra={
              <Text type="secondary">
                Use this email to receive WhatsApp session expiry notifications
                and reconnection instructions.
              </Text>
            }
            rules={[
              { required: true, message: "Enter your email address" },
              {
                type: "email" as const,
                message: "Please enter a valid email address",
              },
            ]}
          >
            <Input placeholder="name@company.com" />
          </Form.Item>
          <Alert
            type="info"
            description={
              <span>
                WhatsApp sessions are time-limited. Once a session expires, an
                email notification with reconnection instructions will be sent
                to the configured email address.
              </span>
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>

        {step === 1 && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                border: "1px dashed #d9d9d9",
                borderRadius: 12,
                minHeight: 240,
                marginBottom: 8,
              }}
            >
              {qrTimedOut ? (
                <Space
                  direction="vertical"
                  align="center"
                  style={{ padding: 16 }}
                >
                  <CloseCircleFilled
                    style={{ color: "#ff4d4f", fontSize: 48 }}
                  />
                  <Text strong>QR Code Expired</Text>
                  <Text type="secondary" style={{ textAlign: "center" }}>
                    The QR code session has timed out. Please generate a new
                    one.
                  </Text>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={handleRetry}
                  >
                    Retry
                  </Button>
                </Space>
              ) : !active ? (
                <QRCode
                  value={valueQR || "TEST"}
                  size={200}
                  statusRender={customStatusRender}
                  status={loadingQR ? "loading" : "active"}
                />
              ) : (
                <QRCode
                  value={"success"}
                  size={200}
                  statusRender={customStatusRender}
                  status={"scanned"}
                />
              )}
            </div>

            {!active && !qrTimedOut && (
              <Row justify="center" style={{ marginBottom: 4 }}>
                <Text type="secondary">
                  QR code refreshes automatically · Expires in{" "}
                  <Text strong>{formatCountdown(remainingSeconds)}</Text>
                </Text>
              </Row>
            )}

            <Row justify="center" style={{ marginBottom: 4 }}>
              <Text type="secondary">
                Once scanned, your WhatsApp will be activated and connected to
                this platform.
              </Text>
            </Row>

            <Form.Item name="clientId" style={{ display: "none" }}>
              <Input />
            </Form.Item>
          </>
        )}
      </Form>

      <Divider />

      <Row gutter={[12, 12]}>
        <Col span={12}>
          {step === 0 ? (
            <Button onClick={onCancel} className="w-100" size="large">
              Cancel
            </Button>
          ) : (
            <Button onClick={handleBack} className="w-100" size="large">
              Back
            </Button>
          )}
        </Col>
        <Col span={12}>
          {" "}
          {step === 0 ? (
            <Button
              size="large"
              type="primary"
              className="w-100"
              disabled={hasErrors}
              onClick={handleContinueEmail}
            >
              Continue
            </Button>
          ) : (
            <Button
              size="large"
              className="w-100"
              type="primary"
              onClick={handleConnect}
              loading={checking}
              disabled={qrTimedOut}
            >
              I’ve Scanned QR Code
            </Button>
          )}
        </Col>
      </Row>
      <CustomModal
        type="error"
        title="Activation Failed"
        message="We couldn’t verify your WhatsApp account. Please try scanning the QR code again."
        isVisible={isErrorModalVisible}
        onClose={() => setIsErrorModalVisible(false)}
        successButtonText="Retry"
        onSuccessClick={() => handleConnect()}
        cancelButtonText="Go Back"
        onCancelClick={() => setIsErrorModalVisible(false)}
      />
    </Modal>
  );
};

export default WhatsAppConfigModal;
